import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import toast from "react-hot-toast";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfDay,
  startOfWeek,
  subMonths
} from "date-fns";
import {
  CalendarCheck2,
  CalendarDays,
  ChevronRight,
  List,
  Loader2,
  Search,
  X
} from "lucide-react";
import { MonthNav } from "@/features/grc/components/MonthNav";
import { MonthYearPicker } from "@/features/grc/components/MonthYearPicker";
import {
  useAttendeeLevels,
  useCalendarEvents,
  useCalendarHolidays,
  useDraftBusinesses,
  useDraftCalendar,
  useDraftCalendarDayMeetings,
  useDraftMeetingTypes,
  useFiscalYears,
  useLocations
} from "@/services/draftCalendar";
import { useCanAddMeeting, useCanEditMeeting } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { CalendarDayMeeting, CalendarEvent, CalendarHoliday, MeetingNameSearchResponse } from "@/types/draftCalendar";
import { exportDraftCalendarToExcel } from "../utils/exportDraftCalendar";
import { MeetingFlyout } from "./MeetingFlyout";
import { CalendarEventSearch } from "./CalendarEventSearch";
import "../DraftCalendarPage.css";
import "./DraftCalendarView.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_EVENTS_PER_DAY = 3;

type EventRangePart = "single" | "start" | "middle" | "end";

function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value.includes("T") ? value : `${value}T00:00:00`);
  return isValid(parsed) ? parsed : null;
}

function getDateKey(value: Date): string {
  return format(value, "yyyy-MM-dd");
}

function getMonthDays(monthDate: Date): Date[] {
  const firstVisible = startOfWeek(startOfMonth(monthDate));
  const lastVisible = endOfWeek(endOfMonth(monthDate));
  const days: Date[] = [];
  let cursor = firstVisible;

  while (cursor <= lastVisible) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }

  return days;
}

function getEventDurationDays(event: CalendarEvent): number {
  const start = parseCalendarDate(event.startDate);
  const end = parseCalendarDate(event.endDate);
  if (!start || !end) return 1;

  const startTime = startOfDay(start).getTime();
  const endTime = startOfDay(end).getTime();
  return Math.max(1, Math.floor((endTime - startTime) / 86_400_000) + 1);
}

function compareCalendarEvents(left: CalendarEvent, right: CalendarEvent): number {
  const leftStart = parseCalendarDate(left.startDate)?.getTime() ?? 0;
  const rightStart = parseCalendarDate(right.startDate)?.getTime() ?? 0;
  if (leftStart !== rightStart) return leftStart - rightStart;

  const durationDifference = getEventDurationDays(right) - getEventDurationDays(left);
  if (durationDifference !== 0) return durationDifference;

  return (left.meetingName ?? "").localeCompare(right.meetingName ?? "");
}

function getEventsForDay(events: CalendarEvent[], day: Date): CalendarEvent[] {
  return events
    .filter((event) => {
      const start = parseCalendarDate(event.startDate);
      const end = parseCalendarDate(event.endDate);
      if (!start || !end) return false;

      return isWithinInterval(startOfDay(day), {
        start: startOfDay(start),
        end: endOfDay(end)
      });
    })
    .sort(compareCalendarEvents);
}

function sortCalendarEvents(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort(compareCalendarEvents);
}

function getEventLanesForCalendar(events: CalendarEvent[], days: Date[]): Map<string, number> {
  const lanes = new Map<string, number>();
  const laneEvents: Array<CalendarEvent | null> = Array(MAX_EVENTS_PER_DAY).fill(null);
  const firstVisibleDay = days[0];

  days.forEach((day) => {
    laneEvents.forEach((event, index) => {
      const end = parseCalendarDate(event?.endDate);
      if (!event || !end || startOfDay(end) < startOfDay(day)) {
        laneEvents[index] = null;
      }
    });

    const startingEvents = sortCalendarEvents(events).filter((event) => {
      if (lanes.has(String(event.id))) return false;

      const start = parseCalendarDate(event.startDate);
      const end = parseCalendarDate(event.endDate);
      if (!start || !end) return false;

      const startsToday = isSameDay(start, day);
      const startedBeforeVisibleGrid = isSameDay(day, firstVisibleDay) && startOfDay(start) < startOfDay(day) && endOfDay(end) >= startOfDay(day);
      return startsToday || startedBeforeVisibleGrid;
    });

    startingEvents.forEach((event) => {
      const freeLane = laneEvents.findIndex((laneEvent) => laneEvent === null);
      if (freeLane === -1) return;

      laneEvents[freeLane] = event;
      lanes.set(String(event.id), freeLane);
    });
  });

  return lanes;
}

function getEventRangePart(event: CalendarEvent, day: Date): EventRangePart {
  const start = parseCalendarDate(event.startDate);
  const end = parseCalendarDate(event.endDate);
  if (!start || !end) return "single";

  const dayTime = startOfDay(day).getTime();
  const startTime = startOfDay(start).getTime();
  const endTime = startOfDay(end).getTime();

  if (startTime === endTime) return "single";
  if (dayTime === startTime) return "start";
  if (dayTime === endTime) return "end";
  return "middle";
}

function shouldShowEventText(event: CalendarEvent, day: Date, rangePart: EventRangePart, currentDate: Date): boolean {
  if (rangePart === "single" || rangePart === "start") return true;

  const start = parseCalendarDate(event.startDate);
  if (!start || startOfDay(start) >= startOfDay(day)) return false;

  const isWeekStartContinuation = day.getDay() === 0;
  const isCurrentMonthStartContinuation = isSameDay(day, startOfMonth(currentDate));
  return isWeekStartContinuation || isCurrentMonthStartContinuation;
}

function getEventRangeClass(rangePart: EventRangePart): string {
  switch (rangePart) {
    case "start":
      return "dcvEventRangeStart";
    case "middle":
      return "dcvEventRangeMiddle";
    case "end":
      return "dcvEventRangeEnd";
    case "single":
    default:
      return "dcvEventRangeSingle";
  }
}

function getHolidayClass(holiday: CalendarHoliday | undefined): string {
  switch (holiday?.calendarHolidayHeaderCss) {
    case "DoNotBlockHolidaysCalendarCss":
      return "dcvHolidayDoNotBlock";
    case "USHolidaysCalendarCss":
      return "dcvHolidayUs";
    case "DeloitteHolidaysCalendarCss":
      return "dcvHolidayDeloitte";
    case "ReligiousHolidaysCalendarCss":
      return "dcvHolidayReligious";
    default:
      return "";
  }
}

function formatPreferredDate(value: string | null | undefined): string {
  const parsed = parseCalendarDate(value);
  return parsed ? format(parsed, "EEE,MM-dd-yyyy") : "";
}

function PreferredDateIcon({ isPrimary }: { isPrimary: boolean }) {
  return (
    <CalendarCheck2
      aria-hidden="true"
      className={isPrimary ? "dcvDateIconPrimary" : "dcvDateIconSecondary"}
      size={16}
    />
  );
}

interface DayMeetingsDialogProps {
  open: boolean;
  date: Date | null;
  meetings: CalendarDayMeeting[];
  isLoading: boolean;
  error: Error | null;
  onClose: () => void;
  onOpenMeeting: (id: number) => void;
}

function DayMeetingsDialog({
  open,
  date,
  meetings,
  isLoading,
  error,
  onClose,
  onOpenMeeting
}: DayMeetingsDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="dcvDialog"
      aria-labelledby="dcv-day-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onClose={onClose}
      onKeyDown={handleKeyDown}
    >
      <div className="dcvDialogPanel">
        <header className="dcvDialogHeader">
          <h2 id="dcv-day-dialog-title">
            {date ? `Meeting for ${format(date, "MMMM dd, yyyy")}` : "Meetings"}
          </h2>
          <button type="button" className="dcvIconButton" onClick={onClose} aria-label="Close meeting list">
            <X size={18} />
          </button>
        </header>

        <div className="dcvDialogBody">
          {isLoading && (
            <div className="dcvState" role="status">
              <Loader2 className="dcvSpin" size={18} />
              Loading meetings...
            </div>
          )}

          {error && !isLoading && (
            <div className="dcvAlert" role="alert">
              Unable to load meetings for this day.
            </div>
          )}

          {!isLoading && !error && meetings.length === 0 && (
            <div className="dcvState">No meetings found for this day.</div>
          )}

          {!isLoading && !error && meetings.length > 0 && (
            <ul className="dcvMeetingList" aria-label="Meetings for selected day">
              {meetings.map((meeting, index) => (
                <li key={meeting.id} className={index % 2 === 0 ? "dcvMeetingRowEven" : "dcvMeetingRowOdd"}>
                  <div className="dcvMeetingSummary">
                    <strong>{meeting.meetingName ?? "Untitled meeting"}</strong>
                    {meeting.preferredDatesDayResponse?.map((dateRange) => (
                      <div key={`${meeting.id}-${dateRange.id ?? dateRange.startDate}`} className="dcvDateRow">
                        <PreferredDateIcon isPrimary={dateRange.isItPrimaryDate} />
                        <span>
                          {formatPreferredDate(dateRange.startDate)} - {formatPreferredDate(dateRange.endDate)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="dcvOpenMeetingButton"
                    onClick={() => onOpenMeeting(meeting.id)}
                    aria-label={`Open ${meeting.meetingName ?? "meeting"}`}
                  >
                    <ChevronRight size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </dialog>
  );
}

export function DraftCalendarView() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const currentUser = useCurrentUser();
  const canAddMeeting = useCanAddMeeting();
  const canEditMeeting = useCanEditMeeting();
  const monthLabelRef = useRef<HTMLButtonElement>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [meetingNameFilter, setMeetingNameFilter] = useState("");
  const [selectedFYId, setSelectedFYId] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [dayMeetings, setDayMeetings] = useState<CalendarDayMeeting[]>([]);
  const [flyoutMeetingId, setFlyoutMeetingId] = useState<number | null>(null);
  const [flyoutViewOnly, setFlyoutViewOnly] = useState(true);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  const { data: events = [], isPending, isFetching, error, refetch } =
    useCalendarEvents(month, year, userEmail, meetingNameFilter);
  const { data: holidays = [], isFetching: isLoadingHolidays } = useCalendarHolidays(month, year);
  const dayMeetingsMutation = useDraftCalendarDayMeetings();

  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const { data: businessesData } = useDraftBusinesses();
  const { data: meetingTypesData } = useDraftMeetingTypes();
  const { data: attendeeLevelsData } = useAttendeeLevels();
  const { data: locationsData } = useLocations();

  const defaultFiscalYear = useMemo(
    () => fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0],
    [fiscalYears]
  );
  const activeFiscalYearId = selectedFYId || defaultFiscalYear?.id || 0;
  const activeFiscalYear = fiscalYears.find((fy) => fy.id === activeFiscalYearId) ?? defaultFiscalYear;
  const isFYClosed = activeFiscalYear?.submissionStatus === "Closed";
  const { data: draftItemsForExport = [] } = useDraftCalendar(activeFiscalYearId, userEmail);

  const days = useMemo(() => getMonthDays(currentDate), [currentDate]);
  const visibleEventLanes = useMemo(
    () => getEventLanesForCalendar(events, days),
    [days, events]
  );
  const holidaysByDate = useMemo(() => {
    const map = new Map<string, CalendarHoliday>();
    holidays.forEach((holiday) => {
      const holidayDate = parseCalendarDate(holiday.date);
      if (holidayDate) map.set(getDateKey(holidayDate), holiday);
    });
    return map;
  }, [holidays]);

  function openMeeting(id: number) {
    setFlyoutMeetingId(id);
    setFlyoutViewOnly(true);
  }

  function openCreate() {
    setFlyoutMeetingId(0);
    setFlyoutViewOnly(false);
  }

  async function openDayMeetings(day: Date, dayEvents: CalendarEvent[]) {
    setSelectedDay(day);
    setDayMeetings([]);

    try {
      const meetings = await dayMeetingsMutation.mutateAsync(dayEvents.map((event) => event.id));
      setDayMeetings(meetings);
    } catch {
      // Mutation state drives the dialog error message.
    }
  }

  function handleDateChange(event: ChangeEvent<HTMLInputElement>) {
    const parsed = parseCalendarDate(event.target.value);
    if (parsed) setCurrentDate(parsed);
  }

  function handleFlyoutSaved() {
    setFlyoutMeetingId(null);
    void refetch();
  }

  const isLoading = isPending && isFetching;
  const filteredCount = events.length;

  return (
    <main className="dcvPage draft-calendar-scroll-page" aria-label="Draft Calendar Schedule View">
      <div className="flex items-center border-b border-slate-200 bg-white px-3 py-2 gap-2">
        {/* Left: Month navigation */}
        <div className="flex flex-none items-center gap-2 shrink-0">
          <MonthNav
            label={format(currentDate, "MMMM yyyy")}
            onPrev={() => setCurrentDate((date) => subMonths(date, 1))}
            onNext={() => setCurrentDate((date) => addMonths(date, 1))}
            monthLabelRef={monthLabelRef}
            onLabelClick={() => {
              if (monthLabelRef.current) {
                setTriggerRect(monthLabelRef.current.getBoundingClientRect());
              }
              setShowMonthPicker(true);
            }}
            showThisMonth
            onThisMonth={() => setCurrentDate(new Date())}
          />
          <div className="mx-1 h-6 w-px bg-slate-200" aria-hidden="true" />
          {(isLoading || isLoadingHolidays) && <Loader2 className="dcvSpin" size={18} aria-label="Loading calendar" />}
        </div>

        {/* Center: Search — CalendarEventSearch mirrors Blazor CalendarEventSearch */}
        <div className="flex flex-1 justify-center">
          <CalendarEventSearch
            userEmail={userEmail}
            value={meetingNameFilter}
            onValueChange={setMeetingNameFilter}
            onSelect={(item) => {
              if (!item) {
                setMeetingNameFilter("");
                return;
              }
              // Navigate to the meeting's start month (mirrors Blazor HandleValueChanged)
              if (item.eventStart) {
                try {
                  const start = new Date(item.eventStart);
                  if (!isNaN(start.getTime())) {
                    setCurrentDate(new Date(start.getFullYear(), start.getMonth(), 1));
                  }
                } catch { /* ignore */ }
              }
              setMeetingNameFilter(item.meetingName ?? "");
            }}
            placeholder="Search"
          />
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex overflow-hidden border border-slate-300">
            <button
              type="button"
              onClick={() => navigate("/draftcalendar")}
              aria-label="List view"
              aria-pressed={false}
              className="flex h-8 w-8 items-center justify-center bg-white text-slate-600 hover:bg-slate-50"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Calendar view"
              aria-pressed={true}
              className="flex h-8 w-8 items-center justify-center border-l border-slate-300 bg-slate-900 text-white"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>

          {(!currentUser || canAddMeeting) && !isFYClosed && (
            <button
              type="button"
              onClick={openCreate}
              className="Add-Request-button border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              Add Meeting
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
        <span>{filteredCount} meeting{filteredCount !== 1 ? "s" : ""}</span>
        {isFetching && !isPending && (
          <span className="flex items-center gap-1 text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Refreshing&hellip;
          </span>
        )}
        {isFYClosed && <span className="rounded bg-red-100 px-2 py-0.5 font-medium text-red-700">Fiscal Year Closed</span>}
      </div>

      {error && !isLoading && (
        <div className="dcvAlert" role="alert">
          Unable to load the calendar schedule.
        </div>
      )}

      <section className="dcvScheduler" aria-label="Monthly calendar">
        <div className="dcvWeekHeader" role="row">
          {WEEKDAYS.map((day) => (
            <div key={day} className="dcvWeekday" role="columnheader">
              {day}
            </div>
          ))}
        </div>

        <div className="dcvGrid" role="grid" aria-label={format(currentDate, "MMMM yyyy")}>
          {days.map((day) => {
            const key = getDateKey(day);
            const holiday = holidaysByDate.get(key);
            const dayEvents = getEventsForDay(events, day);
            const renderableEvents = dayEvents.filter((event) => visibleEventLanes.has(String(event.id)));
            const eventsByLane = Array.from({ length: MAX_EVENTS_PER_DAY }, (_, index) =>
              renderableEvents.find((event) => visibleEventLanes.get(String(event.id)) === index) ?? null
            );
            const hiddenEventCount = dayEvents.length - renderableEvents.length;
            const isOutsideMonth = !isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={key}
                className={`dcvDayCell ${isOutsideMonth ? "dcvDayOutside" : ""}`}
                role="gridcell"
                aria-label={format(day, "EEEE, MMMM d, yyyy")}
              >
                <div className="dcvDayHeader">
                  <span
                    className={`dcvDayNumber ${isToday ? "dcvDayNumberToday" : ""} ${getHolidayClass(holiday)}`}
                    title={holiday?.description || undefined}
                    tabIndex={holiday?.description ? 0 : undefined}
                  >
                    {format(day, "d")}
                    {holiday?.description && (
                      <span className="dcvHolidayTooltip" role="tooltip">
                        {holiday.description.split(",").map((line) => (
                          <span key={line.trim()}>{line.trim()}</span>
                        ))}
                      </span>
                    )}
                  </span>
                </div>

                <div className="dcvEventList">
                  {eventsByLane.map((event, laneIndex) => {
                    if (!event) {
                      return <div key={`${key}-empty-${laneIndex}`} className="dcvEventLaneSpacer" aria-hidden="true" />;
                    }

                    const rangePart = getEventRangePart(event, day);
                    const showText = shouldShowEventText(event, day, rangePart, currentDate);
                    const eventLabel = `${event.allInAttendees ?? 0} | ${event.meetingName ?? "Untitled meeting"}`;

                    return (
                      <button
                        key={`${key}-${event.id}`}
                        type="button"
                        className={`dcvEventItem ${getEventRangeClass(rangePart)} ${showText ? "" : "dcvEventContinuation"}`}
                        onClick={() => openMeeting(event.id)}
                        title={event.meetingName ?? "Untitled meeting"}
                        aria-label={eventLabel}
                      >
                        <span className="dcvEventContent">
                          {showText ? eventLabel : "\u00a0"}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {hiddenEventCount > 0 && (
                  <button
                    type="button"
                    className="dcvMoreButton"
                    onClick={() => void openDayMeetings(day, dayEvents)}
                  >
                    +{hiddenEventCount} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <DayMeetingsDialog
        open={selectedDay !== null}
        date={selectedDay}
        meetings={dayMeetings}
        isLoading={dayMeetingsMutation.isPending}
        error={dayMeetingsMutation.error instanceof Error ? dayMeetingsMutation.error : null}
        onClose={() => {
          setSelectedDay(null);
          setDayMeetings([]);
        }}
        onOpenMeeting={(id) => {
          setSelectedDay(null);
          openMeeting(id);
        }}
      />

      {flyoutMeetingId !== null && (
        <MeetingFlyout
          meetingId={flyoutMeetingId}
          fiscalYear={activeFiscalYear}
          businesses={businessesData ?? {}}
          meetingTypes={meetingTypesData ?? {}}
          attendeeLevels={attendeeLevelsData ?? []}
          locations={locationsData ?? []}
          isViewOnly={flyoutViewOnly}
          lockedBy={null}
          canEdit={canEditMeeting}
          onEdit={() => setFlyoutViewOnly(false)}
          onSaved={handleFlyoutSaved}
          onClose={() => setFlyoutMeetingId(null)}
        />
      )}
      <MonthYearPicker
        isOpen={showMonthPicker}
        currentDate={currentDate}
        onDateSelect={(newDate) => setCurrentDate(newDate)}
        onClose={() => setShowMonthPicker(false)}
        triggerRect={triggerRect}
      />
    </main>
  );
}